/**
 * Rol operativo Vault (consulta, archivista, …) desde `GET /api/auth/me`.
 * Simappe OAuth siempre trae ROLE_DEVELOPER (técnico); no usarlo para permisos Vault.
 */
import {
  isTruthySuperadminFlag,
  normalizeVaultRoleToken,
  sessionJwtIndicatesVaultSuperadmin,
} from './vault-user-roles.util';
import {
  readVaultTenantIdFromAssignmentRow,
  isVaultUuid,
  normalizeVaultTenantId,
} from './vault-tenant-scope.util';
import {
  VAULT_SINGLE_TENANT_MODE,
  persistVaultTenantFromTenantsPayload,
  persistActiveTenantFromAuthMe,
  readActiveTenantIdFromAuthMeRecord,
  resolveOperativeRoleForActiveTenant,
  resolveVaultActiveTenantId,
} from './vault-active-tenant.util';
import {
  readVaultTenantAssignmentsFromStorage,
  replaceVaultTenantRoleInSession,
} from './vault-tenant-assignments.storage';
import { unwrapVaultApiRecord } from './vault-api-unwrap.util';
import { sessionUserIsCanonicalVaultSuperadmin } from './vault-canonical-superadmin.util';
import {
  applyVaultSessionPinToStorage,
  clearVaultSessionPin,
  readVaultSessionPin,
  vaultSessionPinMatchesAuth,
  writeVaultSessionPin,
  VAULT_OPERATIVE_ROLE_PINNED_KEY,
} from './vault-session-pin.util';

export type VaultOperativeQaProfile = 'admin' | 'auditor' | 'archivista' | 'consulta' | 'unknown';

export const VAULT_OPERATIVE_ROLE_KEY = 'vault_operative_role';
export const VAULT_IS_SUPERADMIN_SCOPE_KEY = 'vault_is_superadmin_scope';
export const VAULT_DASHBOARD_ROLE_KEY = 'vault_dashboard_role';
export { VAULT_OPERATIVE_ROLE_PINNED_KEY } from './vault-session-pin.util';

const SIMAPPE_TECHNICAL_ROLE_TOKENS = new Set([
  'developer',
  'role_developer',
  'desarrollo',
  'desarrollador',
]);

const AUTH_ME_PROMOTED_KEYS = [
  'is_superadmin',
  'role',
  'tenant_id',
  'tenantId',
  'tenants',
] as const;

/** Etiqueta legible para roles operativos Vault (JWT / Simappe / tenant). */
export function vaultOperativeRoleLabel(raw: string): string {
  const norm = normalizeVaultRoleToken(raw);
  const labels: Record<string, string> = {
    superadmin: 'Super Administrador',
    super_admin: 'Super Administrador',
    superadministrador: 'Super Administrador',
    admin_empresa: 'Administrador',
    archivista: 'Archivista',
    auditor: 'Auditor',
    consulta: 'Consulta',
    administrador: 'Administrador',
    admin: 'Administrador',
  };
  if (labels[norm]) return labels[norm];
  if (norm.includes('superadmin') || norm.includes('super_admin')) return 'Super Administrador';
  if (norm.includes('admin') && norm.includes('empresa')) return 'Administrador';
  return raw
    .trim()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function authMeRecord(raw: unknown): Record<string, unknown> | null {
  const record = unwrapVaultApiRecord(raw);
  if (!record || !raw || typeof raw !== 'object') return record;
  const root = raw as Record<string, unknown>;
  for (const key of AUTH_ME_PROMOTED_KEYS) {
    if (record[key] === undefined && root[key] !== undefined) {
      record[key] = root[key];
    }
  }
  return record;
}

function readIsSuperadminFromRecord(record: Record<string, unknown>): boolean {
  for (const key of [
    'is_superadmin',
    'isSuperAdmin',
    'is_super_admin',
    'superadmin',
    'superAdmin',
  ] as const) {
    if (isTruthySuperadminFlag(record[key])) return true;
  }
  return false;
}

function isSuperadminFromAuthMePayload(raw: unknown, record: Record<string, unknown>): boolean {
  if (readIsSuperadminFromRecord(record)) return true;

  if (raw && typeof raw === 'object') {
    const root = raw as Record<string, unknown>;
    if (readIsSuperadminFromRecord(root)) return true;
    const nested = root['data'];
    if (nested && typeof nested === 'object') {
      if (readIsSuperadminFromRecord(nested as Record<string, unknown>)) {
        return true;
      }
    }
  }

  try {
    const rawUser = localStorage.getItem('user_data');
    if (rawUser) {
      const u = JSON.parse(rawUser) as Record<string, unknown>;
      if (readIsSuperadminFromRecord(u)) return true;
    }
  } catch {
    /* ignore */
  }

  return false;
}

function operativeRoleFromAuthMe(record: Record<string, unknown>): string {
  const activeTenantId =
    normalizeVaultTenantId(resolveVaultActiveTenantId()) ||
    normalizeVaultTenantId(String(record['tenant_id'] ?? record['tenantId'] ?? ''));

  const tenants = record['tenants'];
  if (Array.isArray(tenants) && tenants.length) {
    if (activeTenantId) {
      for (const t of tenants) {
        if (!t || typeof t !== 'object') continue;
        const row = t as Record<string, unknown>;
        const tid = normalizeVaultTenantId(readVaultTenantIdFromAssignmentRow(row));
        const role = String(row['role'] ?? '').trim();
        if (!role || !tid) continue;
        if (tid === activeTenantId) return role;
      }
    }

    if (VAULT_SINGLE_TENANT_MODE && tenants.length === 1) {
      const only = tenants[0];
      if (only && typeof only === 'object') {
        const role = String((only as Record<string, unknown>)['role'] ?? '').trim();
        if (role) return role;
      }
    }

    for (const t of tenants) {
      if (!t || typeof t !== 'object') continue;
      const role = String((t as Record<string, unknown>)['role'] ?? '').trim();
      if (role) return role;
    }
  }

  const fromAssignments = assignmentRoleForActiveTenant();
  if (fromAssignments) return fromAssignments;

  return '';
}

function dashboardStatsRoleIsSuperadmin(role: string): boolean {
  const norm = normalizeVaultRoleToken(role);
  return (
    norm === 'superadmin' ||
    norm.includes('superadmin') ||
    norm.includes('super_admin') ||
    norm === 'superadministrador'
  );
}

/** Persiste rol Vault tras `GET /api/auth/me` (fuente de verdad para UI/permisos). */
export function persistVaultUserIdFromAuthMe(raw: unknown): void {
  if (typeof localStorage === 'undefined') return;
  const record = authMeRecord(raw);
  if (!record) return;
  const id = String(record['id'] ?? '').trim();
  if (!isVaultUuid(id)) return;
  try {
    const rawUser = localStorage.getItem('user_data');
    const data = rawUser ? (JSON.parse(rawUser) as Record<string, unknown>) : {};
    data['vault_user_id'] = id;
    localStorage.setItem('user_data', JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

/**
 * Id Vault persistido por `/api/auth/me` (`user_data.vault_user_id`).
 * No usar el `id`/`sub` Simappe del JWT para `/file-assignments/assigned` hasta tener este valor.
 */
export function readPersistedVaultUserId(): string {
  if (typeof localStorage === 'undefined') return '';
  try {
    const rawUser = localStorage.getItem('user_data');
    if (!rawUser) return '';
    const data = JSON.parse(rawUser) as Record<string, unknown>;
    const id = String(data['vault_user_id'] ?? '').trim();
    return isVaultUuid(id) ? id : '';
  } catch {
    return '';
  }
}

/** Sincroniza `vault_operative_role` desde my-tenants / asignaciones del tenant activo. */
export function syncVaultOperativeRoleFromTenantAssignments(tenantId?: string): void {
  if (typeof localStorage === 'undefined') return;
  const id = normalizeVaultTenantId(tenantId || resolveVaultActiveTenantId());
  const role = resolveOperativeRoleForActiveTenant(id || resolveVaultActiveTenantId());
  if (!role) return;
  localStorage.setItem(VAULT_OPERATIVE_ROLE_KEY, role);
  localStorage.setItem(VAULT_IS_SUPERADMIN_SCOPE_KEY, 'false');
}

function assignmentRoleForActiveTenant(): string {
  const tenantId = normalizeVaultTenantId(resolveVaultActiveTenantId());
  if (!tenantId) return '';
  const match = readVaultTenantAssignmentsFromStorage().find(
    (row) => normalizeVaultTenantId(row.tenant_id) === tenantId && (row.role ?? '').trim(),
  );
  return match?.role?.trim() || resolveOperativeRoleForActiveTenant(tenantId) || '';
}

export type ApplyVaultOperativeRoleOptions = {
  tenantId?: string;
  tenantName?: string;
  /** false = no fijar rol (p. ej. hidratación desde auth/me). */
  pin?: boolean;
};

export function clearPinnedVaultOperativeRole(): void {
  clearVaultSessionPin();
}

/** No usar rol de dashboard/stats si la sesión Vault ya tiene rol operativo fijado. */
export function resolveVaultDashboardRoleFallback(statsRole?: string | null): string | null {
  if (readVaultOperativeRole()?.trim() || readVaultSessionPin()?.role) {
    return null;
  }
  const trimmed = (statsRole ?? '').trim();
  return trimmed || null;
}

/** Aplica rol operativo Vault en sesión (fuente: Gestión de Usuarios / asignación tenant). */
export function applyVaultOperativeRoleToSession(
  role: string,
  opts?: ApplyVaultOperativeRoleOptions,
): void {
  if (typeof localStorage === 'undefined') return;
  const trimmed = (role ?? '').trim();
  if (!trimmed) return;
  const preserveSuperadmin = sessionJwtIndicatesVaultSuperadmin();
  localStorage.setItem(VAULT_OPERATIVE_ROLE_KEY, trimmed);
  localStorage.setItem(VAULT_DASHBOARD_ROLE_KEY, trimmed);
  if (!preserveSuperadmin) {
    localStorage.setItem(VAULT_IS_SUPERADMIN_SCOPE_KEY, 'false');
    syncSuperadminFlagIntoUserData(false);
  }
  syncVaultOperativeRoleIntoUserData(trimmed);
  if (opts?.pin !== false && !preserveSuperadmin) {
    const tenant_id = (opts?.tenantId ?? normalizeVaultTenantId(resolveVaultActiveTenantId()) ?? '').trim();
    writeVaultSessionPin({
      role: trimmed,
      tenant_id,
      tenant_name: opts?.tenantName,
    });
  }

  const tenantId = (opts?.tenantId ?? '').trim();
  if (tenantId) {
    replaceVaultTenantRoleInSession(tenantId, trimmed, opts?.tenantName);
  }
}

function syncVaultOperativeRoleIntoUserData(role: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return;
    const data = JSON.parse(raw) as Record<string, unknown>;
    data['role'] = role;
    localStorage.setItem('user_data', JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

/** Sincroniza rol operativo Vault desde `tenants[]` de `/api/auth/me` (fuente de verdad para UI/permisos). */
export function persistVaultOperativeRoleFromAuthMe(raw: unknown): void {
  if (typeof localStorage === 'undefined') return;
  const record = authMeRecord(raw);
  if (!record) return;

  const tenants = record['tenants'];
  if (Array.isArray(tenants) && tenants.length) {
    persistVaultTenantFromTenantsPayload(tenants);
  }
  persistActiveTenantFromAuthMe(raw);

  if (sessionUserIsCanonicalVaultSuperadmin()) {
    applyVaultSuperadminScope();
    return;
  }

  const isSuperadmin = isSuperadminFromAuthMePayload(raw, record);

  if (isSuperadmin) {
    applyVaultSuperadminScope();
    return;
  }

  const role = operativeRoleFromAuthMe(record);
  const pin = readVaultSessionPin();
  const authTenantId = readActiveTenantIdFromAuthMeRecord(record);
  if (pin) {
    if (!role) {
      // auth/me incompleto: conservar pin solo como fallback de la misma sesión.
      applyVaultSessionPinToStorage();
    } else if (vaultSessionPinMatchesAuth(role, authTenantId)) {
      clearVaultSessionPin();
      localStorage.setItem(VAULT_OPERATIVE_ROLE_KEY, role);
      localStorage.setItem(VAULT_DASHBOARD_ROLE_KEY, role);
    } else {
      // Rol/tenant de /auth/me distinto al pin → pin ajeno o obsoleto (p. ej. tras
      // expiración de admin y login de consulta). Nunca reaplicar pin de otro usuario.
      clearVaultSessionPin();
      localStorage.setItem(VAULT_OPERATIVE_ROLE_KEY, role);
      localStorage.setItem(VAULT_DASHBOARD_ROLE_KEY, role);
    }
  } else if (role) {
    localStorage.setItem(VAULT_OPERATIVE_ROLE_KEY, role);
    localStorage.setItem(VAULT_DASHBOARD_ROLE_KEY, role);
  } else {
    syncVaultOperativeRoleFromTenantAssignments();
    if (!readVaultOperativeRole()) {
      localStorage.removeItem(VAULT_OPERATIVE_ROLE_KEY);
    }
  }
  localStorage.setItem(VAULT_IS_SUPERADMIN_SCOPE_KEY, 'false');
  syncSuperadminFlagIntoUserData(false);
  const effectiveRole = readVaultOperativeRole();
  if (effectiveRole) {
    syncVaultOperativeRoleIntoUserData(effectiveRole);
  }
}

/** Fija alcance superadmin (sin rol operativo tenant). */
export function applyVaultSuperadminScope(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(VAULT_OPERATIVE_ROLE_KEY);
  localStorage.setItem(VAULT_IS_SUPERADMIN_SCOPE_KEY, 'true');
  syncSuperadminFlagIntoUserData(true);
}

/** Rol operativo desde `GET /api/dashboard/stats` (fallback cuando `/api/auth/me` falla o llega incompleto). */
export function persistVaultDashboardRoleFromStats(role: string | null | undefined): void {
  if (typeof localStorage === 'undefined') return;
  const trimmed = (role ?? '').trim();
  if (!trimmed) return;
  if (readVaultSessionPin()?.role) return;
  if (readVaultOperativeRole()?.trim() && isVaultOperativeScopeHydrated()) {
    return;
  }
  localStorage.setItem(VAULT_DASHBOARD_ROLE_KEY, trimmed);

  if (dashboardStatsRoleIsSuperadmin(trimmed)) {
    const existingRole = readVaultOperativeRole();
    if (
      readVaultIsSuperadminScope() === false &&
      existingRole &&
      !operativeRoleIndicatesSuperadmin(existingRole)
    ) {
      localStorage.setItem(VAULT_DASHBOARD_ROLE_KEY, trimmed);
      return;
    }
    applyVaultSuperadminScope();
    return;
  }

  if (readVaultIsSuperadminScope() === true) return;

  const existingOperative = readVaultOperativeRole();
  if (isVaultOperativeScopeHydrated() && existingOperative) {
    return;
  }

  const assignmentRole = assignmentRoleForActiveTenant();
  if (assignmentRole) {
    localStorage.setItem(VAULT_OPERATIVE_ROLE_KEY, assignmentRole);
  } else {
    localStorage.setItem(VAULT_OPERATIVE_ROLE_KEY, trimmed);
  }
  localStorage.setItem(VAULT_IS_SUPERADMIN_SCOPE_KEY, 'false');
  syncSuperadminFlagIntoUserData(false);
}

export function readVaultDashboardRole(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const role = localStorage.getItem(VAULT_DASHBOARD_ROLE_KEY)?.trim();
  return role || null;
}

export function readVaultOperativeRole(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const role = localStorage.getItem(VAULT_OPERATIVE_ROLE_KEY)?.trim();
  return role || null;
}

/** `null` = aún no hidratado desde Vault API. */
export function readVaultIsSuperadminScope(): boolean | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(VAULT_IS_SUPERADMIN_SCOPE_KEY);
  if (raw === null) return null;
  return raw === 'true';
}

/** `true` cuando `/api/auth/me` ya fijó el alcance superadmin (true o false). */
export function isVaultOperativeScopeHydrated(): boolean {
  return readVaultIsSuperadminScope() !== null;
}

export function operativeRoleIndicatesSuperadmin(role: string): boolean {
  const norm = normalizeVaultRoleToken(role);
  return (
    norm.includes('superadmin') ||
    norm.includes('super_admin') ||
    norm === 'superadministrador'
  );
}

/**
 * Superadmin según hidratación Vault (`vault_is_superadmin_scope` + `vault_operative_role`).
 * `null` = aún no hidratado; hay que usar fallbacks JWT/Simappe.
 */
export function resolveSuperadminFromOperativeScope(): boolean | null {
  const scope = readVaultIsSuperadminScope();
  if (scope === true) return true;
  if (scope === false) {
    const role = readVaultOperativeRole();
    if (role) return operativeRoleIndicatesSuperadmin(role);
    return false;
  }
  return null;
}

function syncSuperadminFlagIntoUserData(isSuperadmin: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    const raw = localStorage.getItem('user_data');
    if (!raw) return;
    const data = JSON.parse(raw) as Record<string, unknown>;
    data['is_superadmin'] = isSuperadmin;
    localStorage.setItem('user_data', JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearVaultOperativeRoleScope(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(VAULT_OPERATIVE_ROLE_KEY);
  localStorage.removeItem(VAULT_IS_SUPERADMIN_SCOPE_KEY);
  localStorage.removeItem(VAULT_DASHBOARD_ROLE_KEY);
  clearVaultSessionPin();
}

export function isSimappeTechnicalRoleToken(token: string): boolean {
  const norm = normalizeVaultRoleToken(token);
  return SIMAPPE_TECHNICAL_ROLE_TOKENS.has(norm);
}

export function mapVaultTenantRoleToQaProfile(role: string): VaultOperativeQaProfile {
  const norm = normalizeVaultRoleToken(role);
  if (norm.includes('superadmin') || norm.includes('super_admin')) return 'admin';
  if (norm === 'admin_empresa' || norm === 'administrador_empresa') return 'admin';
  if (norm === 'archivista') return 'archivista';
  if (norm === 'auditor') return 'auditor';
  if (norm === 'consulta') return 'consulta';
  return 'consulta';
}

/** Admin Vault sin confundir DEVELOPER (contiene substring "admin"). */
export function tokenImpliesVaultAdminRole(token: string): boolean {
  const norm = normalizeVaultRoleToken(token);
  if (!norm || isSimappeTechnicalRoleToken(norm)) return false;
  if (norm === 'consulta' || norm === 'auditor' || norm === 'archivista') return false;
  if (norm.includes('superadmin') || norm.includes('super_admin')) return true;
  if (norm === 'admin_empresa' || norm === 'administrador_empresa') return true;
  if (norm === 'admin' || norm === 'administrador') return true;
  if (norm.startsWith('admin_') && !norm.includes('auditor')) return true;
  return false;
}
